/**
 * Gleichzeitiges Arbeiten — was andere inzwischen geändert haben.
 *
 * ## Warum es das gibt
 *
 * Im Termin mit der HOWOGE am 07.10.2026 (Punkt 19): wer eine Liste offen
 * hat, während jemand anders darin schreibt, sah die Änderung erst nach dem
 * Neuladen der Seite. Beim Speichern schützte die Versionsprüfung zwar davor,
 * eine fremde Änderung zu überschreiben — aber erst im letzten Moment, und
 * nachdem die Arbeit schon getan war.
 *
 * ## Wie es arbeitet
 *
 * Eine Abfrage an `GET /api/aenderungen/_seit?nach=<sk>`, alle 15 Sekunden.
 * Kein Dauerkanal: die Schnittstelle läuft hinter einem Dienst, der ruhende
 * Verbindungen kappt, und eine serverlose Datenbank soll schlafen dürfen.
 *
 * - **Nur im sichtbaren Reiter** und nur, wenn in den letzten zehn Minuten
 *   jemand etwas eingegeben hat. Danach ruht die Abfrage — sonst hielte ein
 *   vergessener Reiter über Nacht die Datenbank wach.
 * - **Sofort** beim Zurückwechseln in den Reiter und bei der ersten Eingabe
 *   nach einer Ruhepause.
 * - **Nicht im Rückblick.** Wer den Stand vom 9. September ansieht, will
 *   keine Änderungen von heute nachgereicht bekommen.
 * - **Zurückhaltung bei 503**: die Datenbank wacht gerade auf; zwei Minuten
 *   Pause statt fünfzehn Sekunden.
 *
 * Was ankommt, wird zweifach weitergegeben:
 *
 * 1. an die Listen — `bestandGeaendert(sammlung, { still: true })`, sie laden
 *    ohne Ladeanzeige nach;
 * 2. an offene Editoren — über `useFremdaenderung`, die daraus den Hinweis
 *    „Diese Rolle wurde um hh:mm von … geändert" machen.
 *
 * Eigene Änderungen melden sich nicht: weder die mit `eigene` gekennzeichneten
 * noch die, deren Vorgangskennung aus diesem Reiter stammt
 * (`klient.ts`, `eigenerVorgang`).
 *
 * Weitere Reiter derselben Person gleichen sich über einen
 * `BroadcastChannel` ab — ohne Umweg über die Schnittstelle und damit ohne
 * die fünfzehn Sekunden.
 */

import { useEffect, useState } from 'react';
import { ApiFehler, eigenerVorgang, holen, standLesen } from './klient.js';
import { BESTAND_GEAENDERT, bestandGeaendert, type Bestandsmeldung } from './haken.js';
import { mandantAusSpeicher } from '../entwurf/mandant.js';
import { OHNE_ANMELDUNG } from '../anmeldung/entwicklung.js';
import { bereich, msal, sitzungAbgelaufen } from '../anmeldung/msal.js';

/* ------------------------------------------------------------ Vertrag */

/** Ein Eintrag aus `GET /api/aenderungen/_seit` — gruppiert je Objekt und Vorgang. */
export interface LiveEintrag {
  sk: number;
  objektart: string;
  objektId: string;
  vorgangId: string;
  vorgang: string;
  /** Nur Feldnamen, nie Werte. */
  felder: string[];
  zeitpunkt: string;
  akteurName: string;
  /** Dasselbe Konto wie die Anfrage. */
  eigene: boolean;
  /** Bei Zuordnungen: die Rolle, das Asset und seine Objektart. */
  rolleId?: string | null;
  asset?: string | null;
  assetTyp?: string | null;
  /** Falls mitgeliefert: die Kategorie des Assets (AVD/Surface-Zwillinge). */
  kategorie?: string | null;
  /** Welche Sammlungen davon betroffen sind — die Listen laden sie nach. */
  sammlungen: string[];
}

interface Antwort {
  letzteSk: number | null;
  eintraege: LiveEintrag[];
}

/* ------------------------------------------------------------ Takt */

const TAKT = 15_000;
/** So lange nach der letzten Eingabe läuft die Abfrage weiter. */
const RUHE_NACH = 10 * 60_000;
/** Pause nach 503 oder einem Ausfall der Verbindung. */
const ZURUECKHALTUNG = 120_000;
/** Zwei Anstöße in kürzerem Abstand ergeben nur eine Abfrage. */
const MINDESTABSTAND = 2_000;

/** Das Ereignis an `window`, auf das `useFremdaenderung` hört. */
const FREMD = 'iammatrix:fremdaenderung';
const KANALNAME = 'iammatrix-live';

/* ------------------------------------------------------------ Zustand */

let gestartet = false;
let mandant = '';
/** Der Stand der letzten Antwort. `null` heißt: noch keine Anfrage. */
let letzteSk: number | null = null;
/**
 * Die Grenze der ersten Antwort. Was darunter liegt, war schon da, als der
 * Reiter aufging — der Lückenschutz der Schnittstelle liefert es trotzdem
 * (alles aus den letzten 120 Sekunden), und es ist keine Neuigkeit.
 */
let anfangsSk: number | null = null;
/** Bereits gesehene Einträge — der Lückenschutz liefert manche doppelt. */
const gesehen = new Set<number>();
let letzteEingabe = Date.now();
let letzteAbfrage = 0;
let pauseBis = 0;
/** Die Pause kam von einer Abweisung (401/403/404) — sie endet beim Zurückwechseln. */
let pauseAbgewiesen = false;
let laeuft = false;
let uhr: number | undefined;
let kanal: BroadcastChannel | null = null;

function zuruecksetzen(): void {
  letzteSk = null;
  anfangsSk = null;
  gesehen.clear();
}

function darfFragen(): boolean {
  if (document.visibilityState !== 'visible') return false;
  if (Date.now() - letzteEingabe > RUHE_NACH) return false;
  /* Im Rückblick gibt es nichts nachzureichen. */
  if (standLesen() !== null) return false;
  if (Date.now() < pauseBis) return false;
  return true;
}

/**
 * Das Token für die Abfrage — **ohne** Umleitung.
 *
 * `tokenHolen` leitet bei einem abgelaufenen Token auf die Anmeldeseite um.
 * Für eine Abfrage im Hintergrund wäre das der falsche Moment: mitten im
 * Tippen verschwände die Seite. Hier gilt: kein Token, keine Abfrage. Der
 * nächste Abruf, den jemand selbst auslöst, erneuert die Anmeldung.
 */
async function tokenStill(): Promise<string | null> {
  if (OHNE_ANMELDUNG) return '';
  const konto = msal.getAllAccounts()[0];
  if (!konto || sitzungAbgelaufen()) return null;
  try {
    const ergebnis = await msal.acquireTokenSilent({ scopes: [bereich], account: konto });
    return ergebnis.accessToken;
  } catch {
    return null;
  }
}

/** Gibt neue Einträge weiter — an Listen, Editoren und, wenn gewünscht, an die übrigen Reiter. */
function verteilen(eintraege: LiveEintrag[], weiterreichen: boolean): void {
  const neu: LiveEintrag[] = [];
  for (const e of eintraege) {
    if (typeof e.sk !== 'number' || gesehen.has(e.sk)) continue;
    gesehen.add(e.sk);
    if (letzteSk === null || e.sk > letzteSk) letzteSk = e.sk;
    if (anfangsSk !== null && e.sk <= anfangsSk) continue;
    if (e.eigene || eigenerVorgang(e.vorgangId)) continue;
    neu.push(e);
  }
  /* Der Satz der gesehenen Kennungen wächst nicht unbegrenzt: der
     Lückenschutz reicht zwei Minuten zurück, ältere Kennungen kommen nicht
     wieder. */
  if (gesehen.size > 2000) {
    const behalten = [...gesehen].sort((a, b) => b - a).slice(0, 1000);
    gesehen.clear();
    for (const sk of behalten) gesehen.add(sk);
  }
  if (neu.length === 0) return;

  const sammlungen = new Set(neu.flatMap((e) => e.sammlungen ?? []));
  for (const s of sammlungen) bestandGeaendert(s, { still: true, fremd: true });
  window.dispatchEvent(new CustomEvent<LiveEintrag[]>(FREMD, { detail: neu }));
  if (weiterreichen) kanal?.postMessage({ art: 'eintraege', mandant, eintraege: neu });
}

async function fragen(): Promise<void> {
  if (laeuft || !darfFragen()) return;
  if (Date.now() - letzteAbfrage < MINDESTABSTAND) return;
  laeuft = true;
  letzteAbfrage = Date.now();
  try {
    const jetzt = mandantAusSpeicher();
    if (jetzt !== mandant) { mandant = jetzt; zuruecksetzen(); }
    const token = await tokenStill();
    if (token === null) return;
    const pfad = '/api/aenderungen/_seit' + (letzteSk === null ? '' : `?nach=${letzteSk}`);
    const antwort = await holen<Antwort>(pfad, { mandant, token });
    if (letzteSk === null) {
      /* Die erste Antwort setzt nur den Anfang. Was vorher war, ist keine
         Neuigkeit. */
      letzteSk = typeof antwort.letzteSk === 'number' ? antwort.letzteSk : 0;
      anfangsSk = letzteSk;
      for (const e of antwort.eintraege ?? []) gesehen.add(e.sk);
      return;
    }
    verteilen(antwort.eintraege ?? [], true);
    if (typeof antwort.letzteSk === 'number' && antwort.letzteSk > letzteSk) {
      letzteSk = antwort.letzteSk;
    }
  } catch (fehler) {
    /* Kein Fehler, den jemand sehen müsste: die Abfrage ist ein Zusatz. Bei
       503 (Datenbank wacht auf), bei fehlender Verbindung und bei einer
       Abweisung hält sie sich zurück. */
    if (fehler instanceof ApiFehler) {
      if (fehler.status === 503 || fehler.status === 0 || fehler.status >= 500) {
        pauseBis = Date.now() + ZURUECKHALTUNG;
        pauseAbgewiesen = false;
      } else if (fehler.status === 401 || fehler.status === 403 || fehler.status === 404) {
        /* Ohne Recht oder ohne Route: bis zum nächsten Wechsel in den
           Reiter ruht die Abfrage — fünfzehn Sekunden später wäre die
           Antwort dieselbe. */
        pauseBis = Date.now() + 10 * ZURUECKHALTUNG;
        pauseAbgewiesen = true;
      }
    } else {
      pauseBis = Date.now() + ZURUECKHALTUNG;
    }
  } finally {
    laeuft = false;
  }
}

function planen(): void {
  if (uhr !== undefined) window.clearTimeout(uhr);
  uhr = window.setTimeout(() => {
    void fragen().finally(planen);
  }, TAKT);
}

/** Sofort fragen — beim Zurückwechseln und nach einer Ruhepause. */
function anstossen(): void {
  void fragen();
}

/**
 * Hängt die Abfrage ein. Einmal je Seite, aus `main.tsx`.
 *
 * Mehrfache Aufrufe sind folgenlos — der Entwicklungsbetrieb lädt Module
 * bei jeder Änderung neu, und zwei Takte nebeneinander fragten doppelt.
 */
export function liveStarten(): void {
  if (gestartet || typeof window === 'undefined') return;
  gestartet = true;
  mandant = mandantAusSpeicher();

  const eingabe = () => {
    const ruhte = Date.now() - letzteEingabe > RUHE_NACH;
    letzteEingabe = Date.now();
    if (ruhte) anstossen();
  };
  for (const art of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
    window.addEventListener(art, eingabe, { passive: true, capture: true });
  }
  window.addEventListener('focus', () => { letzteEingabe = Date.now(); anstossen(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    letzteEingabe = Date.now();
    if (pauseAbgewiesen) { pauseBis = 0; pauseAbgewiesen = false; }
    anstossen();
  });

  /* Eigene Reiter. Ein Schreibvorgang hier meldet sich dort sofort, und
     was die Abfrage hier findet, muss dort nicht noch einmal gefragt
     werden. */
  if (typeof BroadcastChannel !== 'undefined') {
    kanal = new BroadcastChannel(KANALNAME);
    kanal.onmessage = (ereignis: MessageEvent) => {
      const daten = ereignis.data as
        | { art: 'bestand'; mandant: string; sammlung: string }
        | { art: 'eintraege'; mandant: string; eintraege: LiveEintrag[] }
        | null;
      if (!daten || daten.mandant !== mandantAusSpeicher()) return;
      if (daten.art === 'bestand') {
        bestandGeaendert(daten.sammlung, { still: true, fremd: true });
      } else if (daten.art === 'eintraege' && letzteSk !== null) {
        verteilen(daten.eintraege, false);
      }
    };
    window.addEventListener(BESTAND_GEAENDERT, (e: Event) => {
      const meldung = (e as CustomEvent<Bestandsmeldung>).detail;
      if (!meldung || meldung.fremd) return;
      kanal?.postMessage({ art: 'bestand', mandant: mandantAusSpeicher(), sammlung: meldung.sammlung });
    });
  }

  anstossen();
  planen();
}

/* ------------------------------------------------------------ Haken */

/** Woran ein offener Editor erkennt, dass eine Änderung ihn betrifft. */
export interface Beobachtet {
  /** `Rolle`, `Asset`, … — wie in `iam.Aenderung`. */
  objektart: string;
  objektId: string;
  /** Zuordnungen dieser Rolle zählen mit. */
  rolleId?: string | undefined;
  /** Zuordnungen an diesem Asset zählen mit. */
  asset?: { name: string; assetTyp: string; kategorie?: string | undefined } | undefined;
}

function betrifft(e: LiveEintrag, b: Beobachtet): boolean {
  if (e.objektart === b.objektart && e.objektId === b.objektId) return true;
  if (e.objektart !== 'Zuordnung') return false;
  if (b.rolleId && e.rolleId === b.rolleId) return true;
  if (!b.asset || e.asset !== b.asset.name) return false;
  if (e.assetTyp && b.asset.assetTyp && e.assetTyp !== b.asset.assetTyp) return false;
  /* Die Kategorie nur, wenn beide Seiten sie kennen — `_seit` liefert sie
     bisher nicht mit, dann genügen Name und Objektart. */
  if (typeof e.kategorie === 'string' && b.asset.kategorie !== undefined
      && e.kategorie !== b.asset.kategorie) return false;
  return true;
}

/**
 * Die jüngste fremde Änderung an einem offenen Objekt — oder `null`.
 *
 * `quittieren` blendet den Hinweis aus („Weiterarbeiten" oder nach dem
 * Neuladen). Eine weitere Änderung danach meldet sich wieder.
 *
 * `null` als Beobachtung schaltet ab, etwa solange ein Formular neu anlegt:
 * an etwas, das es noch nicht gibt, kann niemand etwas ändern.
 */
export function useFremdaenderung(beobachtet: Beobachtet | null): {
  eintrag: LiveEintrag | null;
  quittieren: () => void;
} {
  const [eintrag, setzeEintrag] = useState<LiveEintrag | null>(null);
  const schluessel = beobachtet
    ? [beobachtet.objektart, beobachtet.objektId, beobachtet.rolleId ?? '',
      beobachtet.asset?.name ?? '', beobachtet.asset?.assetTyp ?? '',
      beobachtet.asset?.kategorie ?? ''].join('\u0001')
    : '';

  useEffect(() => {
    setzeEintrag(null);
    if (!beobachtet) return undefined;
    const hoeren = (e: Event) => {
      const liste = (e as CustomEvent<LiveEintrag[]>).detail ?? [];
      const passend = liste.filter((x) => betrifft(x, beobachtet));
      const juengster = passend[passend.length - 1];
      if (juengster) setzeEintrag(juengster);
    };
    window.addEventListener(FREMD, hoeren);
    return () => window.removeEventListener(FREMD, hoeren);
    /* `schluessel` fasst die Beobachtung zusammen; das Objekt selbst entsteht
       bei jedem Zeichnen neu. */
  }, [schluessel]);

  return { eintrag, quittieren: () => setzeEintrag(null) };
}

/** „14:32" aus dem Zeitpunkt eines Eintrags. */
export function uhrzeit(roh: string): string {
  const d = new Date(roh);
  if (Number.isNaN(d.getTime())) return roh;
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
